// Builds the project/recorder/camera picker tree for the live wall.
//
// This is deliberately a thin wrapper around buildDeviceTree, not a second
// grouper: buildDeviceTree already carries the rules that matter (channel
// order inside a recorder, and showing an orphaned camera standalone rather
// than dropping it when its recorder is out of scope) — see its own comment.
// A parallel implementation here would either duplicate those rules or drift
// from them the next time one changes.

import type { Camera, Device, Project } from "../../lib/api";
import { buildDeviceTree } from "./deviceTree";

/** One line in the rendered picker tree. */
export type CameraTreeRow =
  | { kind: "project"; id: string; project: Project; cameraCount: number; selectedCount: number }
  | { kind: "device"; id: string; device: Device; cameras: Camera[]; enabledCount: number; selectedCount: number }
  | { kind: "camera"; id: string; camera: Camera; isChild: boolean };

/**
 * Groups cameras by project, then applies buildDeviceTree within each
 * project. Only projects with at least one camera the viewer can see are
 * included — an empty project folder is a dead end, same reasoning as
 * LiveGridPage's own visibleProjects filter.
 *
 * Rows are flat (not nested) with the project/device level carrying no
 * indent info of its own — the renderer indents by row kind. This matches
 * CamerasPage's flat-row convention rather than a nested-Collapsible tree.
 */
export function buildCameraTree(
  projects: Project[],
  devices: Device[],
  cameras: Camera[],
  selected: ReadonlySet<string>,
  expandedProjectIds: ReadonlySet<string>,
  expandedDeviceIds: ReadonlySet<string>,
): CameraTreeRow[] {
  const camerasByProject = new Map<string, Camera[]>();
  for (const camera of cameras) {
    const siblings = camerasByProject.get(camera.project_id);
    if (siblings) {
      siblings.push(camera);
    } else {
      camerasByProject.set(camera.project_id, [camera]);
    }
  }

  const rows: CameraTreeRow[] = [];

  const sortedProjects = [...projects].sort((a, b) => a.name.localeCompare(b.name));
  for (const project of sortedProjects) {
    const projectCameras = camerasByProject.get(project.id) ?? [];
    if (projectCameras.length === 0) continue;

    const selectedCount = projectCameras.filter((c) => selected.has(c.id)).length;
    rows.push({
      kind: "project",
      id: project.id,
      project,
      cameraCount: projectCameras.length,
      selectedCount,
    });

    if (!expandedProjectIds.has(project.id)) continue;

    const projectDevices = devices.filter((d) => d.project_id === project.id);
    const deviceRows = buildDeviceTree(projectDevices, projectCameras, expandedDeviceIds);
    for (const row of deviceRows) {
      if (row.kind === "device") {
        rows.push({
          kind: "device",
          id: row.id,
          device: row.device,
          cameras: row.cameras,
          enabledCount: row.enabledCount,
          selectedCount: row.cameras.filter((c) => selected.has(c.id)).length,
        });
      } else {
        rows.push(row);
      }
    }
  }

  return rows;
}
